/**
 * Device-local authority for private lock-screen previews.
 *
 * FCM tokens belong to a browser origin, not a Firebase account. A token can
 * move from one signed-in person to another between the server's ownership
 * check and delivery. The notification therefore carries both safe generic
 * copy and private preview copy, plus the claim generations allowed to reveal
 * that preview. The service worker reveals it only when this device still has
 * one of those exact generations in IndexedDB.
 *
 * IndexedDB is used because the service worker cannot read localStorage. The
 * database constants are duplicated in public/firebase-messaging-sw.js and
 * must stay in lockstep.
 */

const PREVIEW_DB = 'ldg-push-preview';
const PREVIEW_STORE = 'state';
const PREVIEW_KEY = 'current';
const PREVIEW_DB_VERSION = 1;
const PREVIEW_DB_TIMEOUT_MS = 1_200;
const PREVIEW_READ_TIMEOUT_MS = 700;
const PREVIEW_PREF_PREFIX = 'ldg_push_previews_enabled:';

interface PushPreviewIdentity {
  claimId: string;
  uid: string;
  updatedAt: number;
}

function validClaimId(value: unknown): value is string {
  return typeof value === 'string' && value.length >= 16 && value.length <= 128
    && !/[^A-Za-z0-9._:-]/.test(value);
}

function prefKey(uid: string): string {
  return `${PREVIEW_PREF_PREFIX}${uid}`;
}

/** New enrollments show useful previews unless this person turned them off on this phone. */
export function pushPreviewsEnabled(uid: string, failClosed = false): boolean {
  if (!uid) return false;
  try {
    if (failClosed && !window.localStorage) return false;
    return window.localStorage?.getItem(prefKey(uid)) !== '0';
  } catch {
    return !failClosed;
  }
}

/**
 * Foreground delivery reads the same authority as the worker. One short budget
 * covers opening and reading; missing/blocked storage keeps the generic copy.
 * Unlike enrollment, delivery must not create a database or write an identity.
 */
export function readPushPreviewIdentity(): Promise<Pick<PushPreviewIdentity, 'uid' | 'claimId'> | null> {
  return new Promise((resolve) => {
    let settled = false;
    let database: IDBDatabase | null = null;
    const finish = (value: Pick<PushPreviewIdentity, 'uid' | 'claimId'> | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      try { database?.close(); } catch { /* Read failure stays generic. */ }
      resolve(value);
    };
    const timer = window.setTimeout(() => finish(null), PREVIEW_READ_TIMEOUT_MS);
    let request: IDBOpenDBRequest;
    try {
      request = window.indexedDB.open(PREVIEW_DB, PREVIEW_DB_VERSION);
    } catch {
      finish(null);
      return;
    }
    request.onupgradeneeded = () => {
      // Opening a missing database starts a version-change transaction. Abort
      // it so a notification read cannot create or repair device authority.
      try { request.transaction?.abort(); } catch { /* No identity is trusted. */ }
      finish(null);
    };
    request.onerror = () => finish(null);
    request.onblocked = () => finish(null);
    request.onsuccess = () => {
      database = request.result;
      if (settled) {
        try { database.close(); } catch { /* Late open after timeout. */ }
        return;
      }
      try {
        const tx = database.transaction(PREVIEW_STORE, 'readonly');
        const read = tx.objectStore(PREVIEW_STORE).get(PREVIEW_KEY);
        let identity: Pick<PushPreviewIdentity, 'uid' | 'claimId'> | null = null;
        read.onsuccess = () => {
          const value = read.result as Partial<PushPreviewIdentity> | undefined;
          if (typeof value?.uid === 'string' && value.uid && validClaimId(value.claimId)) {
            identity = { uid: value.uid, claimId: value.claimId };
          }
        };
        read.onerror = () => finish(null);
        tx.oncomplete = () => finish(identity);
        tx.onerror = () => finish(null);
        tx.onabort = () => finish(null);
      } catch {
        finish(null);
      }
    };
  });
}

function openPreviewDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
    };
    const succeed = (database: IDBDatabase) => {
      if (settled) return;
      settle();
      resolve(database);
    };
    const fail = (error: Error) => {
      if (settled) return;
      settle();
      reject(error);
    };
    const timer = window.setTimeout(
      () => fail(new Error('preview storage timed out')),
      PREVIEW_DB_TIMEOUT_MS
    );
    if (typeof window.indexedDB === 'undefined' || typeof window.indexedDB.open !== 'function') {
      fail(new Error('preview storage unavailable'));
      return;
    }
    let req: IDBOpenDBRequest;
    try {
      req = window.indexedDB.open(PREVIEW_DB, PREVIEW_DB_VERSION);
    } catch {
      fail(new Error('preview storage unavailable'));
      return;
    }
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(PREVIEW_STORE)) {
        req.result.createObjectStore(PREVIEW_STORE);
      }
    };
    req.onsuccess = () => {
      if (settled) {
        req.result.close();
        return;
      }
      succeed(req.result);
    };
    req.onerror = () => fail(req.error ?? new Error('preview storage failed'));
    req.onblocked = () => fail(new Error('preview storage blocked'));
  });
}

async function putIdentity(identity: PushPreviewIdentity): Promise<boolean> {
  let database: IDBDatabase;
  try {
    database = await openPreviewDb();
  } catch {
    return false;
  }
  return new Promise((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      database.close();
      resolve(ok);
    };
    const timer = window.setTimeout(() => finish(false), PREVIEW_DB_TIMEOUT_MS);
    try {
      const tx = database.transaction(PREVIEW_STORE, 'readwrite');
      const store = tx.objectStore(PREVIEW_STORE);
      // The database open or this transaction may have waited behind an
      // explicit opt-out. Check when the write transaction is actually
      // running, not only before its first await. A queued old setup must
      // never recreate private-preview authority after the person turned it off.
      let wroteIdentity = false;
      const read = store.get(PREVIEW_KEY);
      read.onsuccess = () => {
        if (!pushPreviewsEnabled(identity.uid)) return;
        store.put(identity, PREVIEW_KEY);
        wroteIdentity = true;
      };
      tx.oncomplete = () => finish(wroteIdentity);
      tx.onerror = () => finish(false);
      tx.onabort = () => finish(false);
    } catch {
      finish(false);
    }
  });
}

/** Remove only the expected account/generation so stale cleanup cannot erase a newer claim. */
export async function clearPushPreviewIdentity(
  expectedClaimId: string | null = null,
  expectedUid: string | null = null,
): Promise<boolean> {
  let database: IDBDatabase;
  try {
    database = await openPreviewDb();
  } catch {
    return false;
  }
  return new Promise((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      database.close();
      // A completed transaction is safe even when there was no matching row
      // to remove. A mismatch means a newer account/generation already won.
      resolve(ok);
    };
    const timer = window.setTimeout(() => finish(false), PREVIEW_DB_TIMEOUT_MS);
    try {
      const tx = database.transaction(PREVIEW_STORE, 'readwrite');
      const store = tx.objectStore(PREVIEW_STORE);
      const read = store.get(PREVIEW_KEY);
      read.onsuccess = () => {
        const current = read.result as Partial<PushPreviewIdentity> | undefined;
        const claimMatches = !expectedClaimId || current?.claimId === expectedClaimId;
        const uidMatches = !expectedUid || current?.uid === expectedUid;
        if (claimMatches && uidMatches) {
          store.delete(PREVIEW_KEY);
        }
      };
      tx.oncomplete = () => finish(true);
      tx.onerror = () => finish(false);
      tx.onabort = () => finish(false);
    } catch {
      finish(false);
    }
  });
}

/**
 * Stage the browser generation before the server transfers the origin token.
 * Old-account pushes then fail closed to generic copy during the transfer.
 */
export async function preparePushPreviewIdentity(uid: string, claimId: string): Promise<boolean> {
  if (!uid || !validClaimId(claimId)) return false;
  if (!pushPreviewsEnabled(uid)) {
    await clearPushPreviewIdentity(null, uid);
    return false;
  }
  return putIdentity({ uid, claimId, updatedAt: Date.now() });
}

/** This setting is intentionally local to one phone, not every device on the account. */
export async function setPushPreviewsEnabled(
  uid: string,
  enabled: boolean,
  currentClaimId: string | null
): Promise<boolean> {
  if (!uid) return false;
  let preferenceSaved = false;
  try {
    const storage = window.localStorage;
    const value = enabled ? '1' : '0';
    storage?.setItem(prefKey(uid), value);
    preferenceSaved = storage?.getItem(prefKey(uid)) === value;
  } catch {
    // Still clear worker authority below on opt-out, but never report the
    // setting saved if a later setup could read the old preference again.
  }
  if (!enabled) {
    // This is an account's explicit privacy choice, not stale-operation
    // cleanup. Overlapping setup responses can leave the local claim cache
    // behind the worker's generation. Revoke every generation for this uid,
    // while preserving any other account that now owns the worker identity.
    const cleared = await clearPushPreviewIdentity(null, uid);
    if (!cleared) {
      // Keep the visible preference aligned with the worker when storage could
      // not be changed. The Account page reports the failed privacy change.
      try { window.localStorage?.setItem(prefKey(uid), '1'); } catch { /* no-op */ }
    }
    return cleared && preferenceSaved;
  }
  if (!preferenceSaved) return false;
  return currentClaimId ? preparePushPreviewIdentity(uid, currentClaimId) : true;
}
